import { useMemo } from 'react';
import { useApp } from '../AppProvider';
import { useAuth } from '../../lib/auth';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { resolveComposerAssignee } from '../../lib/utils/defaultActivityAssignee';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';
import { useActivitySave } from '../shared/activity-drawer/useActivitySave';
import { resolveComposerEndDate } from '../shared/timeline/composerDueDate';
import { saveLeadNoteWithAttachments, triggerLeadActivityWorkflows } from './leadDrawerWrites';
import type { EntityAdapter } from '../shared/activity-drawer/types';

/**
 * Salvataggi del composer inline nella cronologia di un lead o cliente.
 *
 * NON è un percorso di persistenza nuovo: la nota passa dalla STESSA funzione usata da
 * `leadDrawerAdapter.saveNote` (`saveLeadNoteWithAttachments`, con lo stesso
 * `pipelineContext`) e l'attività dallo STESSO `useActivitySave` del drawer, con lo stesso
 * `linkageFields` (`lead_id`) e lo stesso trigger di workflow in insert.
 *
 * L'unica differenza è per DESIGN (regola del composer, mockup vista 6b): l'attività veloce
 * non tocca il calendario, quindi `syncOptIn: false`, nessun invitato e nessun provider
 * meeting. Con `syncOptIn` a false `syncGoogleEvent`/`syncMicrosoftEvent` escono subito
 * `skipped`, senza chiamate né toast: l'unica scrittura è la riga `sales_activities`.
 * Per sync, invitati e Meet si usa "Apri nel drawer completo".
 */
export function useLeadComposerSaves(params: { leadId: string; pipelineContext?: string }) {
  const { leadId, pipelineContext } = params;
  const { toast } = useApp();
  const { user } = useAuth();
  const { createNote, uploadNoteAttachment } = useLeadNoteStore();
  const defaultAssigneeSetting = useSettingsStore((s) => s.settings?.activity_default_assignee ?? null);

  const adapter: EntityAdapter = useMemo(
    () => ({
      kind: 'lead',
      entity: { id: leadId },
      // Per il lead entità e cliente coincidono: è la stessa riga di `clients`.
      clientId: leadId,
      entityUrl: () => `${window.location.origin}/leads/${leadId}`,
      linkageFields: () => ({ lead_id: leadId }),
      defaultAssigneeId: null,
      // Il composer non tocca mai fase/assegnatario dell'entità: nessuna validazione,
      // nessun commit (la shell del drawer resta l'unico posto in cui accadono).
      validateEntityChanges: () => null,
      commitEntityChanges: async () => {},
      saveNote: async (html: string, files: File[]) =>
        saveLeadNoteWithAttachments({
          leadId,
          html,
          files,
          pipelineContext,
          createNote,
          uploadNoteAttachment,
          toast,
        }),
      afterActivitySaved: async (activity, safeType, meta) => {
        if (!meta.isInsert) return;
        await triggerLeadActivityWorkflows(leadId, activity, safeType);
      },
    }),
    [leadId, pipelineContext, createNote, uploadNoteAttachment, toast]
  );

  const { saveActivity } = useActivitySave(adapter);

  const saveNote = async (html: string) => {
    await adapter.saveNote(html, []);
  };

  const saveActivityQuick = async (input: { title: string; type: string; dueDate: Date; description?: string }) => {
    const organizationId = await getCurrentUserOrganizationId();
    if (!organizationId) {
      toast.error('Organizzazione non disponibile: riprova');
      throw new Error('organization_id non disponibile');
    }
    const saved = await saveActivity({
      activityData: {
        title: input.title,
        type: input.type,
        status: 'pending',
        startDateTime: input.dueDate,
        endDateTime: resolveComposerEndDate(input.dueDate),
        // Attività veloce: all'utente corrente, salvo assegnatario fisso nelle impostazioni (6bcec0bd).
        assigned_to: resolveComposerAssignee(defaultAssigneeSetting, user?.id || null),
        description: input.description ?? '',
      },
      editingActivity: null,
      syncOptIn: false,
      meetingProvider: 'none',
      selectedAttendees: [],
      invitedCommercial: null,
      meetOrganizer: 'agent',
      includeDescriptionInEvent: false,
      organizationId,
      toast,
    });
    // `saveActivity` ha già mostrato il toast d'errore: il throw serve solo a far
    // conservare al composer il testo digitato.
    if (!saved) throw new Error("Salvataggio dell'attività fallito");
  };

  return { saveNote, saveActivity: saveActivityQuick };
}
